import type {
   WeightEntry as RemoteWeight,
   WeightUpsertResponse,
} from '@fasting-timer/shared'

import type { WeightEntry as LocalWeight } from '../state/weightStore'
import {
   createDeltaSyncRunner,
   readStoredObject,
   resolveSyncStorage,
   writeStoredObject,
   type SyncScheduler,
   type SyncStorage,
} from './deltaSyncRunner'
import type { WeightsApi } from './weightsApi'

const STATE_STORAGE_KEY = 'weightsSyncState'

type PendingWeight = {
   weight: number | null
   deletedAt: string | null
   updatedAt: string
}

/**
 * Persisted sync bookkeeping. `known` is the weight list (ISO timestamp → kg) as
 * of the last reconcile, so a local edit is found by diffing against it; `init`
 * seeds it from the stored list at startup. A null `cursor` means the first full
 * pull has not happened yet; it merges local and server data without inferring
 * deletes. `pending` holds local changes the server has not confirmed.
 */
type WeightsSyncState = {
   cursor: string | null
   known: Record<string, number> | null
   pending: Record<string, PendingWeight>
}

type StampChange = { serverUpdatedAt: string; previousServerUpdatedAt: string | null }

type WeightsSyncDeps = {
   api: Pick<WeightsApi, 'list' | 'upsert'>
   getLocalEntries: () => readonly LocalWeight[]
   /** Replaces the local weight list after remote changes were merged in. */
   setLocalEntries: (entries: LocalWeight[]) => void
   /** Reports a write that moved the `/v1/sync` conflict stamp. */
   onServerStamp: (change: StampChange) => void
   /** Storage for the sync state; defaults to localStorage. */
   storage?: SyncStorage
   /** Scheduler for the debounced push; defaults to `setTimeout`. */
   schedule?: SyncScheduler
   now?: () => number
}

/**
 * Returns a fresh, never-synced state.
 *
 * @returns The empty state.
 */
function emptyState(): WeightsSyncState {
   return { cursor: null, known: null, pending: {} }
}

/**
 * Reads stored sync state, falling back to an empty state on any mismatch.
 *
 * @param storage - The storage, or null.
 * @returns The state.
 */
function readState(storage: SyncStorage | null): WeightsSyncState {
   const record = readStoredObject(
      storage,
      STATE_STORAGE_KEY,
   ) as Partial<WeightsSyncState> | null
   if (!record) {
      return emptyState()
   }
   return {
      cursor: typeof record.cursor === 'string' ? record.cursor : null,
      known: record.known && typeof record.known === 'object' ? record.known : null,
      pending: record.pending && typeof record.pending === 'object' ? record.pending : {},
   }
}

/**
 * Converts an epoch-ms or ISO timestamp to the canonical ISO key the server uses.
 *
 * @param timestamp - The timestamp.
 * @returns The ISO key.
 */
function toKey(timestamp: number | string) {
   return new Date(
      typeof timestamp === 'number' ? timestamp : Date.parse(timestamp),
   ).toISOString()
}

/**
 * Indexes local entries by ISO key.
 *
 * @param entries - The local entries.
 * @returns The weights by key.
 */
function indexLocal(entries: readonly LocalWeight[]) {
   return new Map(entries.map(entry => [toKey(entry.timestamp), entry.weight]))
}

/**
 * Converts a key → weight map back into local entries, newest first.
 *
 * @param byKey - The weights by ISO timestamp.
 * @returns The local entries.
 */
function toLocalEntries(byKey: ReadonlyMap<string, number>): LocalWeight[] {
   return [...byKey]
      .map(([key, weight]) => ({ weight, timestamp: Date.parse(key) }))
      .sort((a, b) => b.timestamp - a.timestamp)
}

/**
 * Queues the differences between the local list and `known` as pending upserts
 * and tombstones, then makes the local list the new `known`.
 *
 * @param state - The sync state (mutated).
 * @param local - The local weights by key.
 * @param nowIso - The edit time.
 */
function queueLocalChanges(
   state: WeightsSyncState,
   local: ReadonlyMap<string, number>,
   nowIso: string,
) {
   const known = state.known ?? {}
   for (const [key, weight] of local) {
      if (known[key] !== weight) {
         state.pending[key] = { weight, deletedAt: null, updatedAt: nowIso }
      }
   }
   for (const key of Object.keys(known)) {
      if (!local.has(key)) {
         state.pending[key] = { weight: null, deletedAt: nowIso, updatedAt: nowIso }
      }
   }
   state.known = Object.fromEntries(local)
}

/**
 * Seeds `known` from the local weights when unset; otherwise re-queues any
 * local change whose pending record another tab overwrote.
 *
 * @param state - The sync state (mutated).
 * @param local - The local weights by key.
 * @param nowMs - The current time.
 */
function seedOrRequeue(
   state: WeightsSyncState,
   local: ReadonlyMap<string, number>,
   nowMs: number,
) {
   if (state.known === null) {
      state.known = Object.fromEntries(local)
   } else {
      queueLocalChanges(state, local, new Date(nowMs).toISOString())
   }
}

/**
 * Merges server entries into the local weights, skipping any a newer pending
 * local edit supersedes.
 *
 * @param state - The sync state (mutated).
 * @param local - The local weights by key (mutated).
 * @param entries - The server entries.
 * @returns True when the local weights changed.
 */
function mergeRemote(
   state: WeightsSyncState,
   local: Map<string, number>,
   entries: readonly RemoteWeight[],
) {
   const known = state.known ?? {}
   let changed = false
   for (const entry of entries) {
      const key = toKey(entry.timestamp)
      const pending = state.pending[key]
      if (pending && Date.parse(pending.updatedAt) >= Date.parse(entry.updatedAt)) {
         continue
      }
      delete state.pending[key]
      if (entry.deletedAt) {
         changed = local.delete(key) || changed
         delete known[key]
      } else if (typeof entry.weight === 'number') {
         changed = changed || local.get(key) !== entry.weight
         local.set(key, entry.weight)
         known[key] = entry.weight
      }
   }
   state.known = known
   return changed
}

/**
 * Queues local entries the server has never seen (first full pull). Local
 * entries are never read as deletes here; only edits recorded since `init` are.
 *
 * @param state - The sync state (mutated).
 * @param local - The local weights by key.
 * @param remote - The server's full list, tombstones included.
 * @param nowIso - The upload's edit time.
 */
function queueLocalOnly(
   state: WeightsSyncState,
   local: ReadonlyMap<string, number>,
   remote: readonly RemoteWeight[],
   nowIso: string,
) {
   const remoteKeys = new Set(remote.map(entry => toKey(entry.timestamp)))
   for (const [key, weight] of local) {
      if (!remoteKeys.has(key) && !state.pending[key]) {
         state.pending[key] = { weight, deletedAt: null, updatedAt: nowIso }
      }
   }
   state.known = Object.fromEntries(local)
}

/**
 * Converts the pending map into the upsert payload.
 *
 * @param pending - The pending changes by key.
 * @returns The transport entries.
 */
function toUpsertEntries(pending: Record<string, PendingWeight>): RemoteWeight[] {
   return Object.entries(pending).map(([timestamp, change]) =>
      change.deletedAt ?
         { timestamp, deletedAt: change.deletedAt, updatedAt: change.updatedAt }
      :  {
            timestamp,
            weight: change.weight ?? 0,
            deletedAt: null,
            updatedAt: change.updatedAt,
         },
   )
}

/**
 * Clears pending changes the server answered, unless a newer local edit replaced
 * them while the request was in flight.
 *
 * @param state - The sync state (mutated).
 * @param sent - The pushed entries.
 * @param response - One batch response.
 */
function settlePushed(
   state: WeightsSyncState,
   sent: readonly RemoteWeight[],
   response: WeightUpsertResponse,
) {
   const sentUpdatedAt = new Map(sent.map(entry => [entry.timestamp, entry.updatedAt]))
   for (const result of response.results) {
      const key = result.timestamp
      if (key && state.pending[key]?.updatedAt === sentUpdatedAt.get(key)) {
         delete state.pending[key]
      }
      if (result.status === 'invalid') {
         console.warn('Weight entry rejected by the server', result)
      }
   }
}

/**
 * Merges server entries and writes the local list back when it changed.
 *
 * @param state - The sync state (mutated).
 * @param deps - The local-list accessors.
 * @param entries - The server entries.
 */
function applyRemote(
   state: WeightsSyncState,
   deps: Pick<WeightsSyncDeps, 'getLocalEntries' | 'setLocalEntries'>,
   entries: readonly RemoteWeight[],
) {
   const local = indexLocal(deps.getLocalEntries())
   if (mergeRemote(state, local, entries)) {
      deps.setLocalEntries(toLocalEntries(local))
   }
}

/**
 * Creates the weights delta-sync runtime: records local edits as pending
 * upserts or tombstones, pushes them in batches, and pulls server changes since
 * the last `serverTime`. A pending local edit newer than the incoming server
 * version survives the pull and is pushed again.
 *
 * @param deps - Local-list accessors, the API client, and optional storage/scheduler.
 * @returns The sync methods.
 */
export function createWeightsSync(deps: WeightsSyncDeps) {
   const storage = resolveSyncStorage(deps.storage)
   const now = deps.now ?? Date.now
   let state = readState(storage)

   /**
    * Pushes every pending change and merges the server's stored versions, so a
    * stale edit is replaced right away rather than waiting for a pull.
    */
   async function push() {
      const sent = toUpsertEntries(state.pending)
      const responses = sent.length > 0 ? await deps.api.upsert(sent) : null
      for (const response of responses ?? []) {
         settlePushed(state, sent, response)
         applyRemote(state, deps, response.entries)
         if (response.serverUpdatedAt) {
            deps.onServerStamp({
               serverUpdatedAt: response.serverUpdatedAt,
               previousServerUpdatedAt: response.previousServerUpdatedAt,
            })
         }
      }
      writeStoredObject(storage, STATE_STORAGE_KEY, state)
   }

   /**
    * Pulls changes since the cursor; the first pull is a full one that also
    * queues local-only entries for upload.
    *
    * @returns True when the server was reachable.
    */
   async function pull() {
      const listed = await deps.api.list({ since: state.cursor })
      if (!listed) {
         return false
      }
      if (state.cursor === null) {
         queueLocalOnly(
            state,
            indexLocal(deps.getLocalEntries()),
            listed.entries,
            new Date(now()).toISOString(),
         )
      }
      applyRemote(state, deps, listed.entries)
      state.cursor = listed.serverTime
      writeStoredObject(storage, STATE_STORAGE_KEY, state)
      return true
   }

   const runner = createDeltaSyncRunner({
      needsBootstrap: () => state.cursor === null,
      pull,
      push,
      label: 'weights',
      schedule: deps.schedule,
   })

   return {
      sync: runner.sync,
      /**
       * Returns whether a sync has something to do without a server change:
       * a first pull or unpushed local edits.
       *
       * @returns True when the next sync should run regardless of the change stamp.
       */
      hasLocalWork() {
         return state.cursor === null || Object.keys(state.pending).length > 0
      },
      /**
       * Seeds `known` from the current local list when it is unset, so later
       * edits (including deletes made before the first sync) can be diffed, and
       * otherwise queues any drift between the stored list and `known`. Call
       * after the stored weights are loaded.
       */
      init() {
         // Re-read: another tab may have written newer bookkeeping since creation.
         state = readState(storage)
         seedOrRequeue(state, indexLocal(deps.getLocalEntries()), now())
         writeStoredObject(storage, STATE_STORAGE_KEY, state)
      },
      /**
       * Diffs the local list against `known`, queues the differences, and
       * schedules a push.
       */
      recordLocalChange() {
         queueLocalChanges(
            state,
            indexLocal(deps.getLocalEntries()),
            new Date(now()).toISOString(),
         )
         writeStoredObject(storage, STATE_STORAGE_KEY, state)
         runner.schedulePush()
      },
      /**
       * Forgets all sync state so the next sync bootstraps (key change / reset).
       * Call `init` once the local list reflects the new account.
       */
      reset() {
         runner.cancel()
         state = emptyState()
         writeStoredObject(storage, STATE_STORAGE_KEY, null)
      },
   }
}
